import { useEffect, useMemo, useRef } from 'react';
import { getFieldErrors } from '@api/client';
import type { FormApi } from '../components/Form/FormProvider';

/**
 * Maps a backend field key (as emitted by the server) to one or more UI error
 * targets. Targets that match a `<form.Field name="X">` are written into
 * TanStack form state via `setFieldMeta`; targets listed in `baseErrorTargets`
 * are returned for the component to render directly.
 *
 * The mapping table is the single place that knows about backend → UI
 * translation: name mismatches (`catalogues` → `catalogue_ids`), collapsed
 * fields (`tags` → diet/allergen/nutrition), and keys with no form field
 * (`images`). Pass a stable reference (module-level constant or memoized) to
 * avoid retriggering the form-write effect on every render.
 */
export type ServerErrorMap<Target extends string> = Record<string, readonly Target[]>;

export interface UseServerErrorsOptions<Target extends string> {
  error: Error | null | undefined;
  formRef: React.MutableRefObject<FormApi | null>;
  mapServerFieldToTargets: ServerErrorMap<Target>;
  baseErrorTargets: readonly Target[];
  transformMessage?: (target: Target, message: string) => string;
}

export interface UseServerErrorsResult<Target extends string> {
  fieldErrors: Partial<Record<Target, string[]>>;
  baseErrors: Partial<Record<Target, string[]>>;
}

export function useServerErrors<Target extends string>({
  error,
  formRef,
  mapServerFieldToTargets,
  baseErrorTargets,
  transformMessage,
}: UseServerErrorsOptions<Target>): UseServerErrorsResult<Target> {
  const { fieldErrors, baseErrors } = useMemo<UseServerErrorsResult<Target>>(() => {
    if (!error) return { fieldErrors: {}, baseErrors: {} };

    const rawErrors = getFieldErrors(error);
    if (!rawErrors) return { fieldErrors: {}, baseErrors: {} };

    const baseSet = new Set<Target>(baseErrorTargets);
    const nextField: Partial<Record<Target, string[]>> = {};
    const nextBase: Partial<Record<Target, string[]>> = {};

    for (const [serverKey, rawMessage] of Object.entries(rawErrors)) {
      const targets = mapServerFieldToTargets[serverKey];
      if (!targets || targets.length === 0) continue;

      for (const target of targets) {
        const message = transformMessage
          ? transformMessage(target, rawMessage)
          : rawMessage;
        if (baseSet.has(target)) {
          (nextBase[target] ??= []).push(message);
        } else {
          (nextField[target] ??= []).push(message);
        }
      }
    }

    return { fieldErrors: nextField, baseErrors: nextBase };
  }, [error, mapServerFieldToTargets, baseErrorTargets, transformMessage]);

  const writtenFieldKeysRef = useRef<Target[]>([]);

  useEffect(() => {
    const form = formRef.current;
    if (!form) {
      writtenFieldKeysRef.current = [];
      return;
    }

    // Clear any previously written onSubmit messages first so stale errors
    // don't linger when the latest 422 payload omits a previously erroring key.
    for (const target of writtenFieldKeysRef.current) {
      form.setFieldMeta(target as string, (prev) => {
        const { onSubmit: _onSubmit, ...rest } = prev.errorMap ?? {};
        return { ...prev, errorMap: rest };
      });
    }
    writtenFieldKeysRef.current = [];

    for (const target of Object.keys(fieldErrors) as Target[]) {
      const messages = fieldErrors[target];
      if (!messages || messages.length === 0) continue;
      // TanStack's errorMap.onSubmit takes a single value. Multi-message
      // targets are surfaced via the returned `fieldErrors` for any caller
      // that wants to render the full list.
      const [message] = messages;
      form.setFieldMeta(target as string, (prev) => ({
        ...prev,
        errorMap: { ...prev.errorMap, onSubmit: { message } },
      }));
      writtenFieldKeysRef.current.push(target);
    }
  }, [fieldErrors, formRef]);

  return { fieldErrors, baseErrors };
}
